import { ImagePaths } from '../../lib/imageUrls.js';

export const sc400InformationProtectionAdministrator = {
  id: 53,
  slug: 'sc-400-information-protection-administrator',
  ogImage: '/og/courses/sc-400-information-protection-administrator.jpg',
  courseCode: 'SC-400-001',
  featured: false,
  title: 'SC-401 Microsoft Information Security Administrator (formerly SC-400)',
  subtitle: 'Classify, protect and govern data at scale with Microsoft Purview',

  valueProposition: 'Data classification and protection failures are the root cause of most Australian Notifiable Data Breaches. This programme trains information protection administrators to implement Microsoft Purview sensitivity labels, DLP policies, retention labels, and insider risk controls — mapped to the Privacy Act 1988, Australian Privacy Principles, and APRA CPS 234 data governance requirements — and prepares candidates for the SC-401 Information Security Administrator certification exam (the current credential that replaced SC-400).',

  duration: '32 hours (4 days intensive or 8 weeks part-time)',
  students: 'New',
  level: 'Intermediate',
  organization: 'Microsoft',
  image: ImagePaths.COURSE_3,
  shortDescription: 'SC-400-001 — Microsoft Information Security Administrator (SC-401) certification covering Purview sensitivity labels, DLP, retention, and insider risk for Australian data protection practitioners.',

  price: {
    aud: 1499,
    currency: 'AUD',
    earlyBird: 1199
  },

  stripeConfig: {
    priceId: 'price_1U3WMpKu1nyc322UCd9m50k1',
    productId: 'prod_V3deoMN85c7fie'
  },

  snapshot: {
    whoItsFor: [
      'Compliance officers and data governance managers implementing Microsoft Purview',
      'IT administrators responsible for DLP, sensitivity labels, and retention policies',
      'Privacy professionals mapping Privacy Act 1988 obligations to technical controls',
      'Security engineers preparing for the SC-401 Microsoft Associate certification (formerly SC-400)'
    ],
    accreditation: 'Preparation for Microsoft Certified: Information Security Administrator Associate (Exam SC-401, which replaced SC-400)'
  },

  logistics: {
    classSize: 'Maximum 12 participants with individual Microsoft 365 E5 Compliance lab access',
    requirements: [
      'Working knowledge of Microsoft 365 administration and basic compliance concepts',
      'Microsoft 365 E5 Compliance trial or existing Purview portal access',
      'Understanding of your organisation\'s data classification requirements is beneficial',
      'Familiarity with SharePoint, Exchange, and Teams data flows'
    ]
  },

  overview: 'The SC-401 Microsoft Information Security Administrator programme covers the implementation and management of data protection controls using Microsoft Purview. Microsoft retired the SC-400 Information Protection and Compliance Administrator exam on 31 May 2025 and replaced it with SC-401 Administering Information Security in Microsoft 365 — this programme teaches the current SC-401 skills outline. The curriculum addresses the exam skill domains: implement information protection, implement data loss prevention, and manage data security, lifecycle and insider risk across Microsoft 365. In the Australian regulatory context, every technical implementation is mapped to the Australian Privacy Principles — in particular APP 11 (security of personal information), APP 6 (use and disclosure), and APP 8 (cross-border disclosure) — and to APRA CPS 234 information security classification requirements for entities regulated by the Australian Prudential Regulation Authority. The programme also covers how Purview controls integrate with Microsoft 365 Copilot to prevent oversharing of sensitive content during AI-assisted work, which is a rapidly growing compliance requirement for Australian organisations deploying Copilot.',

  outcomes: [
    'Design and implement a sensitivity label taxonomy for Australian organisations covering personal, commercial, and regulated information',
    'Configure auto-labelling policies using trainable classifiers and sensitive information types (TFN, Medicare, passport)',
    'Implement Data Loss Prevention policies for Exchange, SharePoint, Teams, Endpoints, and Copilot',
    'Configure communication compliance policies for regulatory monitoring obligations',
    'Design retention labels and policies aligned to Australian record-keeping legislation',
    'Implement Records Management for PSPF and APRA-regulated entities',
    'Configure Insider Risk Management policies for data exfiltration and policy violation scenarios',
    'Pass the Microsoft SC-401 Information Security Administrator Associate exam (formerly SC-400)'
  ],

  audience: [
    'Information protection and compliance administrators operating Microsoft Purview',
    'Privacy officers translating Privacy Act 1988 and APP obligations into technical controls',
    'Data governance managers designing sensitivity label and retention taxonomies',
    'Microsoft 365 administrators taking on DLP, retention and eDiscovery responsibilities',
    'Records managers in government entities working to the Protective Security Policy Framework',
    'Security engineers preparing the data governance groundwork for a Microsoft 365 Copilot rollout'
  ],

  prerequisites: [
    'Working knowledge of Microsoft 365 administration — Exchange Online, SharePoint Online and Teams',
    'Basic understanding of compliance concepts: classification, retention, legal hold and eDiscovery',
    'Microsoft 365 E5 Compliance trial or existing Microsoft Purview portal access for labs',
    'Compliance Administrator or Compliance Data Administrator role in the lab tenant',
    'Familiarity with your organisation\'s data classification requirements is beneficial but not required'
  ],

  modules: [
    {
      number: 1,
      title: 'Implement Information Protection with Sensitivity Labels',
      duration: '8 hours',
      topics: [
        'Sensitivity label taxonomy design for Australian organisations: classification scheme for personal, commercial, and government information',
        'Configure sensitivity labels: encryption, content marking, and visual indicators',
        'Deploy auto-labelling for Exchange, SharePoint, and OneDrive using sensitive information types',
        'Trainable classifiers: built-in and custom classifiers for Australian document types',
        'Sensitivity labels and Microsoft Copilot: Restricted Content Discovery and data oversharing prevention'
      ]
    },
    {
      number: 2,
      title: 'Implement Data Loss Prevention',
      duration: '8 hours',
      topics: [
        'DLP policy design: priority, scoping, conditions, actions, and exception management',
        'Australian sensitive information types: Tax File Number, Medicare number, passport, ABN detection',
        'Endpoint DLP: device controls, clipboard restrictions, and removable storage policies',
        'Microsoft Teams DLP: protecting sensitive content in chat, channel messages, and meetings',
        'Copilot DLP: blocking AI-assisted summarisation and generation of highly confidential content'
      ]
    },
    {
      number: 3,
      title: 'Data Lifecycle Management and Records Management',
      duration: '8 hours',
      topics: [
        'Retention label and retention policy design for Australian legislation: Privacy Act 1988, Tax Administration Act, corporations law',
        'Adaptive scopes: dynamic retention targeting based on user attributes',
        'Records management: file plans, regulatory records, and disposition review workflows',
        'PSPF (Protective Security Policy Framework) records management for government entities',
        'Content explorer and activity explorer: data mapping and compliance evidence collection'
      ]
    },
    {
      number: 4,
      title: 'Insider Risk, Communication Compliance, and eDiscovery',
      duration: '8 hours',
      topics: [
        'Insider Risk Management: risky users, data theft, and policy violation indicator tuning',
        'Communication Compliance: financial services regulatory monitoring and workplace policy enforcement',
        'Microsoft Purview eDiscovery: content search, Core, and Premium for litigation hold and legal requests',
        'Privacy Act 1988 and APRA CPS 234 compliance evidence: Content Explorer, audit logs, and reports',
        'SC-401 exam preparation: domain reviews, practice questions, and mock exam'
      ]
    }
  ],

  format: {
    type: 'Live online training with individual Purview lab tenants — 4 days intensive or 8 weeks part-time',
    includes: [
      '32 hours of instructor-led instruction with individual Microsoft 365 E5 Compliance lab access',
      'Sensitivity label taxonomy workshop producing a classification scheme you can deploy',
      'Australian sensitive information type labs: Tax File Number, Medicare, passport and ABN detection',
      'DLP, retention and records management policy templates mapped to Australian legislation',
      'Insider Risk Management and Communication Compliance policy configuration walkthroughs',
      'Timed SC-401 mock exam with domain reviews and answer rationale',
      'Recorded sessions and lab guides for 6 months',
      'Certificate of completion and 30 days of post-course email support'
    ]
  },

  nextIntake: {
    date: 'Register your interest — we will confirm the next cohort',
    details: 'Four-day intensive cohorts run monthly; the 8-week part-time stream runs one evening per week for compliance teams who cannot release staff for a full week'
  },

  certification: 'Prepares participants for Microsoft Certified: Information Security Administrator Associate via Exam SC-401 (Administering Information Security in Microsoft 365). SC-401 replaced the retired SC-400 Information Protection and Compliance Administrator exam on 31 May 2025. Exam voucher purchased separately.',

  faqs: [
    {
      question: 'Which Microsoft licence is needed for this course?',
      answer: 'Microsoft 365 E5 Compliance or the standalone Microsoft Purview Information Protection and Compliance add-on covers all features taught in this programme, including sensitivity labels, DLP, retention, Insider Risk Management, and Communication Compliance. A 90-day Microsoft 365 E5 trial is the most cost-effective lab option for individual learners.'
    },
    {
      question: 'Is this course relevant for Copilot governance?',
      answer: 'Directly relevant. The programme covers how sensitivity labels interact with Microsoft Copilot responses, how DLP policies block Copilot from surfacing or generating content labelled Highly Confidential, and how Restricted Content Discovery limits which SharePoint sites surface in Copilot. This is the foundational technical work required before deploying Copilot at scale.'
    },
    {
      question: 'Does the course cover Australian record-keeping obligations?',
      answer: 'Yes. Module 3 maps retention labels and retention policies to Australian legislation: the Privacy Act 1988 (mandatory retention periods for personal information), the Tax Administration Act (tax record retention), the Corporations Act (financial records), and APRA CPS 234 (information security records for regulated entities). We also cover PSPF records management for government participants.'
    },
    {
      question: 'How does this course relate to the Purview Retention at Scale and Purview Data Protection Practitioners courses?',
      answer: 'This programme covers the full SC-401 exam blueprint including retention and DLP, but at exam-preparation depth. The Purview Retention at Scale and Purview Data Protection Practitioners courses go deeper into operational implementation beyond the exam — covering advanced scenarios, edge cases, and operational runbooks not tested in SC-401. SC-401 is the right starting point; the operational courses are follow-on specialisations.'
    }
  ],

  australianContext: true,
  complianceFrameworks: ['Privacy Act 1988', 'OAIC APPs', 'APRA CPS 234', 'ASD Essential Eight', 'PSPF']
};
